let minicartDeliveryTimer;

const ukTimeZone = 'Europe/London';
const defaultNextDayDeliveryCutoffTime = '20:00';
const deliveryMessageRefreshDelay = 60000;
const minutesPerHour = 60;
const freeDeliveryLeadDays = 2;
const workingDays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];
const monthNames = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];
const ordinalSuffixes = ['th', 'st', 'nd', 'rd'];
const teensRangeStart = 11;
const teensRangeEnd = 13;
const ordinalDivisor = 10;
const dateSegmentLength = 2;

const getUkTimeParts = () => {
  const formatter = new Intl.DateTimeFormat('en-GB', {
    timeZone: ukTimeZone,
    weekday: 'short',
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
    hour12: false
  });

  return formatter.formatToParts(new Date()).reduce((parts, part) => {
    if (part.type !== 'literal') parts[part.type] = part.value;
    return parts;
  }, {});
};

const getOrdinalSuffix = (day) => {
  const remainder = day % ordinalDivisor;
  const isTeen = day % 100 >= teensRangeStart && day % 100 <= teensRangeEnd;
  if (isTeen) return ordinalSuffixes[0];
  return ordinalSuffixes[remainder] || ordinalSuffixes[0];
};

const parseBankHolidays = (value) => {
  if (!value) return new Set();
  return new Set(
    value
      .split(',')
      .map(date => date.trim())
      .filter(date => /^\d{4}-\d{2}-\d{2}$/.test(date))
  );
};

const toDateKey = (date) => {
  const month = String(date.getUTCMonth() + 1).padStart(dateSegmentLength, '0');
  const day = String(date.getUTCDate()).padStart(dateSegmentLength, '0');
  return `${date.getUTCFullYear()}-${month}-${day}`;
};

// Build a UTC date from the UK calendar date so day arithmetic stays
// timezone-agnostic (we only care about the date, not the time of day).
const toUtcDate = (parts) => new Date(Date.UTC(
  Number(parts.year),
  Number(parts.month) - 1,
  Number(parts.day)
));

const getFreeDeliveryDate = (parts, bankHolidays = new Set()) => {
  const deliveryDate = toUtcDate(parts);

  const sunday = 0;
  const saturday = 6;
  const isWorkingDay = () =>
    deliveryDate.getUTCDay() !== sunday &&
    deliveryDate.getUTCDay() !== saturday &&
    !bankHolidays.has(toDateKey(deliveryDate));

  // Step forward day by day, counting only working days, until the full
  // lead time has elapsed. Weekends and bank holidays in the window extend
  // the date rather than being counted.
  let workingDaysElapsed = 0;
  while (workingDaysElapsed < freeDeliveryLeadDays) {
    deliveryDate.setUTCDate(deliveryDate.getUTCDate() + 1);
    if (isWorkingDay()) workingDaysElapsed += 1;
  }

  const day = deliveryDate.getUTCDate();
  const month = monthNames[deliveryDate.getUTCMonth()];
  // Append the year only when the delivery date rolls into the next calendar
  // year, so "2nd January" near a year boundary isn't ambiguous.
  const wrapsYear = deliveryDate.getUTCFullYear() > Number(parts.year);
  const yearSuffix = wrapsYear ? ` ${deliveryDate.getUTCFullYear()}` : '';
  return `${day}${getOrdinalSuffix(day)} ${month}${yearSuffix}`;
};

const parseCutoffTime = (cutoffTime) => {
  const match = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(cutoffTime);
  if (!match) return { hour: 20, minute: 0 };

  return {
    hour: Number(match[1]),
    minute: Number(match[2])
  };
};

const getDeliveryState = (cutoffTime = defaultNextDayDeliveryCutoffTime, bankHolidays = new Set()) => {
  const parts = getUkTimeParts();
  const weekday = parts.weekday;
  const hour = Number(parts.hour);
  const minute = Number(parts.minute);
  const second = Number(parts.second);
  const cutoff = parseCutoffTime(cutoffTime);
  // A weekday bank holiday is still a non-working day — treat it like a weekend.
  const isWorkingDay = workingDays.includes(weekday) && !bankHolidays.has(toDateKey(toUtcDate(parts)));
  const currentMinutes = (hour * minutesPerHour) + minute;
  const cutoffMinutes = (cutoff.hour * minutesPerHour) + cutoff.minute;
  const isBeforeCutoff = currentMinutes < cutoffMinutes;

  if (!isWorkingDay) return { type: 'weekend' };
  // After cutoff on a working day means the evening window (cutoff → midnight):
  // promote the order-now message with the free delivery date.
  if (!isBeforeCutoff) return { type: 'order-now', date: getFreeDeliveryDate(parts, bankHolidays) };

  const remainingMinutes = cutoffMinutes - currentMinutes - (second > 0 ? 1 : 0);
  const hoursLeft = Math.max(0, Math.floor(remainingMinutes / minutesPerHour));
  const minutesLeft = Math.max(0, remainingMinutes % minutesPerHour);

  return {
    type: 'countdown',
    time: `${hoursLeft} hr${hoursLeft === 1 ? '' : 's'} ${minutesLeft} min${minutesLeft === 1 ? '' : 's'}`
  };
};

const updateMinicartDeliveryMessage = () => {
  document.querySelectorAll('[data-cart-delivery-message]').forEach(message => {
    const title = message.querySelector('[data-cart-delivery-title]');
    const text = message.querySelector('[data-cart-delivery-text]');
    if (!title || !text) return;

    // Any pre-order line item pins the fixed pre-order message, regardless of
    // cutoff time, weekends, or bank holidays.
    if (message.dataset.hasPreorder === 'true' && message.dataset.preorderHtml) {
      title.textContent = message.dataset.preorderTitle || '';
      text.innerHTML = message.dataset.preorderHtml;
      return;
    }

    const summary = message.closest('form, [data-minicart-render]')
      ?.querySelector('[data-cart-delivery-summary]');
    const bankHolidays = parseBankHolidays(message.dataset.bankHolidays);
    const deliveryState = getDeliveryState(message.dataset.cutoffTime, bankHolidays);

    if (summary && message.dataset.summaryNextWorkingDay) {
      summary.textContent = message.dataset.summaryNextWorkingDay;
    }

    if (deliveryState.type === 'countdown') {
      title.textContent = message.dataset.countdownTitle;
      text.innerHTML = message.dataset.countdownHtml.replace(/\{\{ time \}\}/g, deliveryState.time);
      return;
    }

    if (deliveryState.type === 'weekend') {
      title.textContent = message.dataset.weekendTitle;
      text.innerHTML = message.dataset.weekendHtml;
      return;
    }

    title.textContent = message.dataset.orderNowTitle;
    text.innerHTML = message.dataset.orderNowHtml.replace(/\{\{ date \}\}/g, deliveryState.date);
  });
};

const initMinicartDeliveryMessage = () => {
  clearInterval(minicartDeliveryTimer);
  updateMinicartDeliveryMessage();
  minicartDeliveryTimer = setInterval(updateMinicartDeliveryMessage, deliveryMessageRefreshDelay);
};

const destroyMinicartDeliveryMessage = () => {
  clearInterval(minicartDeliveryTimer);
};

export default initMinicartDeliveryMessage;
export { destroyMinicartDeliveryMessage };
